/** Allowed DMS mime types (mirrors mof-strapi document-mime.ts). */
export const ALLOWED_DOCUMENT_MIMES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

const DOCUMENT_EXT_MIMES: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

/** Matches typical server rule: alphanumeric, dots, hyphens, underscores only. */
const SAFE_FILENAME_PATTERN = /^[a-zA-Z0-9._-]+$/;

/** Infer a document mime from extension when the browser reports an empty/octet-stream type. */
export function resolveDocumentMime(
  file: Pick<File, "name" | "type">,
): string {
  if (file.type && file.type !== "application/octet-stream") return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();
  return (ext && DOCUMENT_EXT_MIMES[ext]) || file.type;
}

export function isAllowedDocumentFile(
  file: Pick<File, "name" | "type">,
): boolean {
  const mime = resolveDocumentMime(file);
  return Boolean(mime && ALLOWED_DOCUMENT_MIMES.has(mime));
}

function sanitizePart(part: string, fallback: string): string {
  const s = part
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^\.+|\.+$/g, "");
  return s || fallback;
}

/**
 * Map any Unicode / spaced filename to ASCII-safe [a-zA-Z0-9._-] for multipart uploads.
 */
export function sanitizeUploadFilename(originalName: string): string {
  const name = originalName.trim() || "file";
  const lastDot = name.lastIndexOf(".");
  const hasExt = lastDot > 0 && lastDot < name.length - 1;
  const extRaw = hasExt ? name.slice(lastDot) : "";
  const baseRaw = hasExt ? name.slice(0, lastDot) : name;

  let ext = sanitizePart(extRaw, "");
  if (ext && !ext.startsWith(".")) {
    ext = `.${ext}`;
  }
  const base = sanitizePart(baseRaw, "file");

  let out = `${base}${ext}`;
  if (!SAFE_FILENAME_PATTERN.test(out)) {
    out =
      ext && SAFE_FILENAME_PATTERN.test(`x${ext}`) ? `file${ext}` : "file.bin";
  }
  if (out.length > 255) {
    out = out.slice(0, 255);
  }
  return out;
}

/** Normalize filename + mime before multipart upload (Arabic names, missing browser mime). */
export function fileWithSafeUploadName(file: File): File {
  const safe = sanitizeUploadFilename(file.name);
  const mime = resolveDocumentMime(file);
  if (safe === file.name && mime === file.type) {
    return file;
  }
  try {
    return new File([file], safe, {
      type: mime || file.type,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}
